import { t } from '@lingui/core/macro';
import { OUTPUT_HEIGHT, OUTPUT_WIDTH } from 'modules/match-video/domain/assembler/constants';
import { ReviewCanvasContext, ReviewScene } from 'modules/match-video/domain/interfaces';
import {
  blockHeight,
  blockWidth,
  Box,
  browserSceneEnv,
  drawContained,
  drawRisingBlock,
  fillBlock,
  measureBlock,
  releaseSceneImage,
  SceneEnv,
  TextBlock,
} from 'modules/match-video/domain/scenes/kit';
import { GoalPlateData, SceneTeam } from 'modules/match-video/domain/scenes/scene-data';
import { beginBody, clipTagWipe, drawTag } from './plate-parts';
import { nightGoalPlateState } from './plate-state';
import { NIGHT, NIGHT_FONTS, PLATE_BOTTOM, PLATE_LEFT, whiteAlpha } from './style';

// `.a-plate` and its parts, px of the 1920×1080 frame.
const HEIGHT = 150;
const TOP = OUTPUT_HEIGHT - PLATE_BOTTOM - HEIGHT;
const CENTER_Y = TOP + HEIGHT / 2;
const RIGHT_LIMIT = OUTPUT_WIDTH - PLATE_LEFT;
const TAG = { size: 54, min: 36, maxWidth: 600, padX: 34, spacing: 1 };
const BODY = { padLeft: 30, padRight: 40, gap: 26 };
const LOGO = { size: 96, image: 72 };
/** The outlined number sits in a fixed 128 × 96 box, baseline 80 px down — the mockup's `<svg>`. */
const NUMBER = { width: 128, height: 96, baseline: 80, size: 76, dash: 420, stroke: 3 };
const PLAYER = { size: 48, min: 36, lineHeight: 1.1 };
const TEAM = { size: 28, min: 22, lineHeight: 1.4 };
const MINUTE = { size: 44, min: 30, maxWidth: 300, padLeft: 26, rule: 2 };
/** The text column's masks inherit the body's 15 px font, so their `.08em` padding is 1.2 px. */
const TEXT_MASK_EM = 15;
const GLOSS = { width: 110, overhang: 20, skew: Math.tan((-20 * Math.PI) / 180) };

export const NIGHT_GOAL_PLATE_FONTS: readonly string[] = [
  NIGHT_FONTS.tag(TAG.size),
  NIGHT_FONTS.number(NUMBER.size),
  NIGHT_FONTS.player(PLAYER.size),
  NIGHT_FONTS.plateTeam(TEAM.size),
  NIGHT_FONTS.minute(MINUTE.size),
];

interface GoalPlateLayout {
  tag: Box;
  tagText: TextBlock;
  body: Box;
  logo: { cx: number; cy: number };
  number: { left: number; text: string } | null;
  player: TextBlock | null;
  team: TextBlock;
  text: { left: number; top: number; width: number };
  minute: { left: number; block: TextBlock } | null;
}

function layoutGoalPlate(m: ReviewCanvasContext, data: GoalPlateData & { team: SceneTeam }): GoalPlateLayout {
  const tagText = measureBlock(m, t`Гол`.toLocaleUpperCase(), {
    maxWidth: TAG.maxWidth,
    size: TAG.size,
    min: TAG.min,
    font: NIGHT_FONTS.tag,
    lineHeight: HEIGHT / TAG.size,
    spacing: TAG.spacing,
  });
  const tag: Box = { left: PLATE_LEFT, top: TOP, width: blockWidth(tagText) + 2 * TAG.padX, height: HEIGHT };
  const bodyLeft = tag.left + tag.width;

  const number = data.player?.number === undefined ? null : String(data.player.number);
  const minuteBlock = data.minuteLabel
    ? measureBlock(m, data.minuteLabel, { maxWidth: MINUTE.maxWidth, size: MINUTE.size, min: MINUTE.min, font: NIGHT_FONTS.minute })
    : null;
  const minuteWidth = minuteBlock ? MINUTE.rule + MINUTE.padLeft + blockWidth(minuteBlock) : 0;

  // Body items in a row with 26 px gaps: logo, [number], text, [minute]. The text gets what is left up to the margin.
  const items = [LOGO.size, ...(number ? [NUMBER.width] : []), ...(minuteBlock ? [minuteWidth] : [])];
  const fixed = BODY.padLeft + BODY.padRight + items.reduce((sum, w) => sum + w, 0) + items.length * BODY.gap;
  const textMax = Math.max(0, RIGHT_LIMIT - bodyLeft - fixed);

  const player = data.player
    ? measureBlock(m, data.player.name, {
        maxWidth: textMax,
        size: PLAYER.size,
        min: PLAYER.min,
        font: NIGHT_FONTS.player,
        lineHeight: PLAYER.lineHeight,
      })
    : null;
  const team = measureBlock(m, data.team.name, {
    maxWidth: textMax,
    size: TEAM.size,
    min: TEAM.min,
    font: NIGHT_FONTS.plateTeam,
    lineHeight: TEAM.lineHeight,
  });
  const textWidth = Math.max(player ? blockWidth(player) : 0, blockWidth(team));
  const maskPad = 0.08 * TEXT_MASK_EM;
  const textHeight = (player ? blockHeight(player) + maskPad : 0) + blockHeight(team) + maskPad;

  let x = bodyLeft + BODY.padLeft;
  const logo = { cx: x + LOGO.size / 2, cy: CENTER_Y };
  x += LOGO.size + BODY.gap;
  const numberBox = number ? { left: x, text: number } : null;
  if (number) x += NUMBER.width + BODY.gap;
  const text = { left: x, top: CENTER_Y - textHeight / 2, width: textWidth };
  x += textWidth;
  const minute = minuteBlock ? { left: x + BODY.gap, block: minuteBlock } : null;
  if (minute) x += BODY.gap + minuteWidth;

  return {
    tag,
    tagText,
    body: { left: bodyLeft, top: TOP, width: x + BODY.padRight - bodyLeft, height: HEIGHT },
    logo,
    number: numberBox,
    player,
    team,
    text,
    minute,
  };
}

/** `.gloss`: a skewed white highlight sweeping across the tag, clipped by it. */
function drawGloss(ctx: ReviewCanvasContext, tag: Box, glossX: number): void {
  const height = tag.height + 2 * GLOSS.overhang;
  ctx.save();
  ctx.translate(tag.left + glossX + GLOSS.width / 2, tag.top + tag.height / 2);
  ctx.transform(1, 0, GLOSS.skew, 1, 0, 0);
  const gradient = ctx.createLinearGradient(-GLOSS.width / 2, 0, GLOSS.width / 2, 0);
  gradient.addColorStop(0, whiteAlpha(0));
  gradient.addColorStop(0.5, whiteAlpha(0.75));
  gradient.addColorStop(1, whiteAlpha(0));
  ctx.fillStyle = gradient;
  ctx.fillRect(-GLOSS.width / 2, -height / 2, GLOSS.width, height);
  ctx.restore();
}

/**
 * Set A goal plate: the lime «ГОЛ» tag wipes in, the dark body unfolds out of it, the logo pops, the number draws
 * itself as an outline, the scorer and the team rise from under a line, the minute slides in; a gloss sweeps the tag.
 * Exit: the body folds into the tag, the tag wipes out to the left. No scorer — no player line and no number.
 */
export async function createNightGoalPlate(data: GoalPlateData, env: SceneEnv = browserSceneEnv): Promise<ReviewScene> {
  const [, logoImage] = await Promise.all([env.loadFonts(NIGHT_GOAL_PLATE_FONTS), env.loadImage(data.team?.logoUrl)]);
  const measurer = env.createCanvas(1, 1);
  // No team — an unpaired goal: only the tag is drawn, the body is laid out empty and skipped.
  const tagOnly = !data.team;
  const layout = layoutGoalPlate(measurer.ctx, { ...data, team: data.team ?? { name: '' } });
  measurer.release();
  const { tag, body, text } = layout;

  return {
    draw: (ctx, timeSec, windowSec) => {
      const s = nightGoalPlateState(timeSec, windowSec);
      ctx.textBaseline = 'alphabetic';
      ctx.textAlign = 'left';

      ctx.save();
      const bodyGradient = ctx.createLinearGradient(body.left, 0, body.left + body.width, 0);
      bodyGradient.addColorStop(0, NIGHT.ink);
      bodyGradient.addColorStop(1, NIGHT.bodyEnd);
      if (!tagOnly && beginBody(ctx, body, s.bodyScaleX, bodyGradient)) {
        if (s.logoScale > 0) {
          ctx.save();
          ctx.translate(layout.logo.cx, layout.logo.cy);
          ctx.scale(s.logoScale, s.logoScale);
          ctx.fillStyle = NIGHT.white;
          ctx.beginPath();
          ctx.arc(0, 0, LOGO.size / 2, 0, Math.PI * 2);
          ctx.fill();
          if (logoImage) drawContained(ctx, logoImage, -LOGO.image / 2, -LOGO.image / 2, LOGO.image, LOGO.image);
          ctx.restore();
        }

        if (layout.number && s.numberDashOffset < NUMBER.dash) {
          ctx.save();
          ctx.font = NIGHT_FONTS.number(NUMBER.size);
          ctx.strokeStyle = NIGHT.lime;
          ctx.lineWidth = NUMBER.stroke;
          ctx.setLineDash([NUMBER.dash]);
          ctx.lineDashOffset = s.numberDashOffset;
          ctx.strokeText(layout.number.text, layout.number.left, CENTER_Y - NUMBER.height / 2 + NUMBER.baseline);
          ctx.restore();
        }

        if (s.textAlpha > 0) {
          ctx.save();
          ctx.globalAlpha = s.textAlpha;
          let top = text.top;
          if (layout.player) {
            ctx.fillStyle = NIGHT.white;
            drawRisingBlock(
              ctx,
              layout.player,
              { x: text.left, top, width: text.width, align: 'left', maskEm: TEXT_MASK_EM },
              s.playerRise,
            );
            top += blockHeight(layout.player) + 0.08 * TEXT_MASK_EM;
          }
          ctx.fillStyle = NIGHT.plateTeam;
          drawRisingBlock(ctx, layout.team, { x: text.left, top, width: text.width, align: 'left', maskEm: TEXT_MASK_EM }, s.teamRise);
          ctx.restore();
        }

        if (layout.minute && s.minute.alpha > 0) {
          const { block, left } = layout.minute;
          const top = CENTER_Y - block.lineHeight / 2;
          ctx.save();
          ctx.globalAlpha = s.minute.alpha;
          ctx.translate(s.minute.dx, 0);
          ctx.fillStyle = NIGHT.minuteRule;
          ctx.fillRect(left, top, MINUTE.rule, block.lineHeight);
          ctx.fillStyle = NIGHT.lime;
          fillBlock(ctx, block, left + MINUTE.rule + MINUTE.padLeft, top, 'left');
          ctx.restore();
        }
      }
      ctx.restore();

      if (s.tagReveal > 0) {
        ctx.save();
        clipTagWipe(ctx, tag, s.tagReveal, true);
        drawTag(ctx, tag, layout.tagText, TAG.padX, NIGHT.lime);
        drawGloss(ctx, tag, s.glossX);
        ctx.restore();
      }
    },
    dispose: () => releaseSceneImage(logoImage),
  };
}
